import { uniq } from "lodash";
import { stringify } from "qs";
import { useQueryString } from "src/design-system/core/hooks";
import { PATH_MARKETING_ACTION, PATH_OFFER, PATH_SPONSORED_ADS } from "src/routesConstants";
import { MarketingActionType } from "src/types/enum/MarketingActionType";
import { MarketingActionVariantEnum } from "src/utils/variants";

export const MARKETING_ACTION_ID_QS_KEY = "marketing-action-id";
export const MARKETING_ACTION_ACTION_ON_PAGE_QS_KEY = "marketing-action-action-on-page";
export const MARKETING_ACTION_VARIANT_QS_KEY = "marketing-action-variant";
export const MARKETING_ACTION_SCENARIO_MODAL_QS_KEY = "marketing-action-scenario-modal";
export const MARKETING_ACTION_OFFER_CHARACTERISTIC_PRODUCT_MODAL_QS_KEY =
    "marketing-action-offer-characteristic-product-modals";
export const MAX_UNLINKED_DATE_UTC_QS_KEY = "max-unlinked-date-utc";

export enum ActionOnPageEnum {
    Create = "create",
    Duplicate = "duplicate",
    Update = "update",
    Template = "template",
}

export interface MarketingActionRouteParams {
    variants?: MarketingActionVariantEnum[];
    autoRule?: boolean;
    id?: number;
    action?: ActionOnPageEnum;
    maxUnlinkedDateUTC?: string | undefined;
}
export const getCorrectPreviousPath = (variants: MarketingActionVariantEnum[]): string => {
    return variants.includes(MarketingActionVariantEnum.SponsoredAd)
        ? PATH_SPONSORED_ADS
        : PATH_OFFER;
};

function buildPath(
    type: MarketingActionType | MarketingActionType.Duo,
    {
        id,
        action = ActionOnPageEnum.Create,
        autoRule = false,
        variants,
        maxUnlinkedDateUTC,
    }: MarketingActionRouteParams = {}
) {
    return `${PATH_MARKETING_ACTION}${stringify(
        {
            [MARKETING_ACTION_ID_QS_KEY]: id,
            [MARKETING_ACTION_ACTION_ON_PAGE_QS_KEY]: action,
            [MAX_UNLINKED_DATE_UTC_QS_KEY]: maxUnlinkedDateUTC,
            [MARKETING_ACTION_VARIANT_QS_KEY]: variants ?? [
                type === MarketingActionType.Offer && MarketingActionVariantEnum.Offer,
                type === MarketingActionType.SponsoredAd && MarketingActionVariantEnum.SponsoredAd,
                type === MarketingActionType.Duo && MarketingActionVariantEnum.Duo,
                autoRule && MarketingActionVariantEnum.AutoRule,
            ],
        },
        { addQueryPrefix: true }
    )}`;
}

export const marketingActionRoutes = {
    offer: (params?: MarketingActionRouteParams): string =>
        buildPath(MarketingActionType.Offer, params),
    ad: (params?: MarketingActionRouteParams): string =>
        buildPath(MarketingActionType.SponsoredAd, params),
    duo: (params?: MarketingActionRouteParams): string =>
        buildPath(MarketingActionType.Duo, params),
};

export const useMarketingActionPathSelection = (): {
    marketingActionId: number;
    setMarketingActionId: (marketingActionId: number) => void;
    actionOnPage: ActionOnPageEnum;
    setActionOnPage: (action: ActionOnPageEnum) => void;
    variants: MarketingActionVariantEnum[];
    setVariants: (variants: MarketingActionVariantEnum[]) => void;
    addVariant: (variant: MarketingActionVariantEnum) => void;
    removeVariant: (variant: MarketingActionVariantEnum) => void;
    maxUnlinkedDateUTC: string;
    setMaxUnlinkedDateUTC: (maxUnlinkedDateUTC: string) => void;
} => {
    const [maxUnlinkedDateUTC, setMaxUnlinkedDateUTC] = useQueryString(
        MAX_UNLINKED_DATE_UTC_QS_KEY,
        undefined
    );
    const [marketingActionId, setMarketingActionId] = useQueryString(MARKETING_ACTION_ID_QS_KEY, 0);
    const [actionOnPage, setActionOnPage] = useQueryString(
        MARKETING_ACTION_ACTION_ON_PAGE_QS_KEY,
        ActionOnPageEnum.Create
    );
    const [variants, setVariants] = useQueryString(MARKETING_ACTION_VARIANT_QS_KEY, []);

    function addVariant(newVariant: MarketingActionVariantEnum) {
        setVariants(uniq([...variants, newVariant]));
    }

    function removeVariant(removeVariant: MarketingActionVariantEnum) {
        const filterVariants = variants.filter((elm) => elm !== removeVariant);
        setVariants(uniq([...filterVariants]));
    }
    return {
        marketingActionId,
        setMarketingActionId,
        actionOnPage,
        setActionOnPage,
        variants,
        setVariants,
        addVariant,
        removeVariant,
        maxUnlinkedDateUTC,
        setMaxUnlinkedDateUTC,
    };
};

export enum OfferFormModalEnum {
    products = "products",
    scenario = "scenario",
}

export function useOfferCharacteristicProductModal(): {
    offerCharacteristicProductModal: OfferFormModalEnum;
    setOfferCharacteristicProductModal: (
        offerCharacteristicProductModal: OfferFormModalEnum
    ) => void;
} {
    const [offerCharacteristicProductModal, setOfferCharacteristicProductModal] = useQueryString(
        MARKETING_ACTION_OFFER_CHARACTERISTIC_PRODUCT_MODAL_QS_KEY,
        undefined
    );

    return { offerCharacteristicProductModal, setOfferCharacteristicProductModal };
}

export function useOfferAddScenarioModal(): {
    offerAddScenarioModal: OfferFormModalEnum;
    setOfferAddScenarioModal: (scenarioId: number) => void;
} {
    const [offerAddScenarioModal, setOfferAddScenarioModal] = useQueryString(
        MARKETING_ACTION_SCENARIO_MODAL_QS_KEY,
        undefined
    );

    return { offerAddScenarioModal, setOfferAddScenarioModal };
}
